/**
 * Enhanced API Client using Axios
 *
 * Provides a comprehensive API client with:
 * - Automatic JWT token handling
 * - Request/response interceptors
 * - Error handling and 401 redirect
 * - TypeScript support for all backend endpoints
 *
 * All endpoints map to /api/v2/* backend routes
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// Use relative URLs in browser to leverage Vite proxy (avoids mixed content issues in WebKit/Safari)
// Vite proxy forwards /api/* to http://localhost:3000/api/*
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Create axios instance with defaults
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL ? `${API_BASE_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds (onboarding can take time)
});

// Request interceptor - Add auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing token to avoid multiple simultaneous refresh calls
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor - Handle 401 errors with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // If 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await apiClient.post('/auth/refresh', {
          token: refreshToken,
        });

        const { token: newToken } = response.data;
        localStorage.setItem('authToken', newToken);

        // Update the authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        processQueue(null, newToken);
        isRefreshing = false;

        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        // Refresh failed - clear tokens (don't redirect, let React Router handle it)
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');

        // Don't redirect - just reject the promise
        // Components should handle 401 errors gracefully
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// TypeScript Interfaces for API Requests/Responses
// ============================================================================

// Auth
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
}

// Onboarding
export interface StartTellerOnboardingRequest {
  enrollmentId: string;
  accessToken?: string;
  institutionId?: string;
  accounts?: any[];
}

export interface OnboardingStatusResponse {
  onboardingId: string;
  status: 'initiated' | 'connecting' | 'importing' | 'categorizing' | 'analyzing' | 'completed' | 'error';
  progress: number;
  message?: string;
  baseline?: {
    safeToSpend: number;
    lifeCosts: number;
    workCosts: number;
    playCosts: number;
    monthlyIncome: number;
    totalExpenses: number;
  };
  error?: string;
}

export interface CompleteBankConnectionResponse {
  success: boolean;
  allAccountsHealthy: boolean;
  message: string;
  accounts: unknown[];
  baseline: {
    created: boolean;
    transactionsAnalysed: number;
    monthsOfData?: number;
    reason?: string;
  };
}

export interface TellerLinkTokenResponse {
  linkToken: string;
  expiration: string;
}

// Traffic Light Decision
export interface TrafficLightRequest {
  amount: number;
  description: string;
  category?: string;
  controllability?: 'fixed' | 'variable' | 'discretionary' | 'unknown' | 'committed' | 'semi';
  paymentMethod?: 'cash' | 'debit' | 'credit_card';
  creditCardId?: string;
  includeDetailedAnalysis?: boolean;
}

export interface TrafficLightResponse {
  decision: 'green' | 'yellow' | 'red';
  advisoryType?: 'warning' | 'informational';
  controllability?: 'fixed' | 'variable' | 'discretionary' | 'unknown';
  confidence: number;
  reasoning: string;
  safeToSpend: number;
  purchaseImpact: {
    immediate: number;
    nextWeek: number;
    nextMonth: number;
  };
  recommendation: string;
  detailedAnalysis?: {
    categorySpending?: any;
    alternativeSuggestions?: string[];
    riskFactors?: string[];
  };
}

export interface TrafficLightBatchResponse {
  success: boolean;
  decisions: Array<TrafficLightResponse & { purchaseId: string }>;
  totalProcessingTime: string;
  averageProcessingTime: string;
}

// Financial Profile
export interface FinancialProfile {
  userId: string;
  monthlyIncome: number;
  lifeCosts: number;
  workCosts: number;
  playCosts: number;
  fixedExpenses: number;
  variableExpenses: number;
  safeToSpend: number;
  lastUpdated: string;
}

export interface CashFlowForecast {
  userId: string;
  weeks: number;
  projections: Array<{
    weekNumber: number;
    startDate: string;
    endDate: string;
    expectedIncome: number;
    expectedExpenses: number;
    projectedBalance: number;
  }>;
  confidence: number;
}

// Transactions
export interface Transaction {
  id: string;
  userId: string;
  description: string;
  amount: number;
  date: string;
  category?: string;
  categoryConfidence?: number;
  merchantName?: string;
  accountId?: string;
  pending?: boolean;
}

export interface TransactionListParams {
  page?: number;
  limit?: number;
  category?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

export interface TransactionUpdateRequest {
  category?: string;
  description?: string;
  amount?: number;
}

export interface MerchantCategoryBreakdown {
  merchantName: string;
  normalizedMerchantName: string;
  total: number;
  count: number;
}

export interface CategoryMerchantBreakdown {
  category: 'life_costs' | 'work_costs' | 'play_costs' | 'uncategorised';
  startDate?: string;
  endDate?: string;
  categoryTotal: number;
  transactionCount: number;
  merchants: MerchantCategoryBreakdown[];
}

// Forecast Unlock
export interface ForecastUnlockStatus {
  currentTier: 'default' | 'bronze' | 'silver' | 'gold';
  maxWeeks: number;
  nextTier: 'bronze' | 'silver' | 'gold' | null;
  progressPercent: number;
  progressDays: number;
  requiredDays: number;
  unlockedAt: string;
  tierDisplayName: string;
  nextTierDisplayName: string | null;
  metrics: {
    consecutiveDaysWithinBudget: number;
    appEngagementDays: number;
    recommendationsFollowed: number;
    streakStartDate: string | null;
  };
}

export interface ForecastTierConfig {
  tier: 'default' | 'bronze' | 'silver' | 'gold';
  maxWeeks: number;
  requiredDays: number;
  displayName: string;
  description: string;
}

export interface ForecastEvaluationResult {
  upgraded: boolean;
  previousTier: 'default' | 'bronze' | 'silver' | 'gold';
  newTier: 'default' | 'bronze' | 'silver' | 'gold';
  status: {
    currentTier: string;
    maxWeeks: number;
    nextTier: string | null;
    progressPercent: number;
    tierDisplayName: string;
  };
  message: string;
}

// Intelligence
export interface SpendingPattern {
  category: string;
  averageAmount: number;
  frequency: string;
  confidence: number;
  lastSeen: string;
}

export interface UserInsight {
  type: 'warning' | 'tip' | 'success';
  title: string;
  message: string;
  actionable: boolean;
  action?: {
    label: string;
    endpoint: string;
  };
}

// Subscription Analysis
export interface SubscriptionAnalysisFilters {
  search?: string;
  category?: string;
  confidence?: string;
}

export interface SubscriptionAnalysisItem {
  id: string;
  vendor?: string;
  description?: string;
  category?: string;
  amountCents?: number;
  monthlyAmountCents?: number;
  annualAmountCents?: number;
  confidence?: number;
  cadence?: string;
  lastChargeDate?: string;
  nextExpectedDate?: string;
}

export interface SubscriptionAnalysisAlert {
  id: string;
  subscriptionId?: string;
  vendor?: string;
  severity?: 'info' | 'warning' | 'critical';
  type?: string;
  message: string;
  date?: string;
}

export interface SubscriptionAnalysisRollup {
  key?: string;
  name?: string;
  category?: string;
  vendor?: string;
  monthlyTotalCents?: number;
  annualTotalCents?: number;
  count?: number;
}

export interface SubscriptionAnalysisResponse {
  subscriptions?: SubscriptionAnalysisItem[];
  monthlyTotalCents?: number;
  annualTotalCents?: number;
  confidenceCounts?: Record<string, number>;
  categoryRollups?: SubscriptionAnalysisRollup[];
  vendorRollups?: SubscriptionAnalysisRollup[];
  alerts?: SubscriptionAnalysisAlert[];
  summary?: {
    monthlyTotalCents?: number;
    annualTotalCents?: number;
    confidenceCounts?: Record<string, number>;
    categoryRollups?: SubscriptionAnalysisRollup[];
    vendorRollups?: SubscriptionAnalysisRollup[];
  };
}

export type BusinessHealthGrade = 'A' | 'B' | 'C' | 'D' | 'F';

export interface BusinessHealthReportHistory {
  id: string;
  userId: string;
  overallScore: number;
  grade: BusinessHealthGrade;
  generatedAt: string;
}

export interface BusinessHealthReportTrendPoint {
  date: string;
  score: number;
  grade: BusinessHealthGrade | string;
}

// ============================================================================
// API Methods - Organized by Feature
// ============================================================================

export const api = {
  // ----------------------------------------------------------------------------
  // Authentication
  // ----------------------------------------------------------------------------
  auth: {
    login: (credentials: LoginRequest) =>
      apiClient.post<AuthResponse>('/auth/login', credentials),

    register: (data: RegisterRequest) =>
      apiClient.post<AuthResponse>('/auth/register', data),

    logout: () =>
      apiClient.post('/auth/logout'),

    // 2026-07-22: backend route is POST /auth/refresh (not /auth/refresh-token)
    // and expects the refresh token in a field called `token`, not
    // `refreshToken` — see AuthController.refreshToken(). This mismatch,
    // combined with AuthController.login() not returning a refreshToken at
    // all, meant every 401 anywhere in the app (e.g. mid-flow during bank
    // connect) permanently failed with "No refresh token available" and the
    // refresh endpoint would have 400'd even if a token had been stored.
    refreshToken: (token: string) =>
      apiClient.post<{ token: string }>('/auth/refresh', { token }),
  },

  // ----------------------------------------------------------------------------
  // Onboarding
  // ----------------------------------------------------------------------------
  onboarding: {
    checkStatus: () =>
      apiClient.get<{
        success: boolean;
        hasCompletedOnboarding: boolean;
        progress: number;
        completedSteps: string[];
        remainingSteps: string[];
        message: string;
        profileCreatedAt: string | null;
      }>('/onboarding/check-status'),

    // Bank connection via Stripe Financial Connections (replaces Teller)
    createBankSession: () =>
      apiClient.post<{
        success: boolean;
        clientSecret: string;
        sessionId: string;
        publishableKey: string;
      }>('/onboarding/bank-session'),

    completeBankConnection: (data: { sessionId: string }) =>
      apiClient.post<CompleteBankConnectionResponse>(
        '/onboarding/bank-complete',
        data,
      ),

    startCSV: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post<{ onboardingId: string }>('/onboarding/start-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    getStatus: (onboardingId: string) =>
      apiClient.get<OnboardingStatusResponse>(`/onboarding/status/${onboardingId}`),

    getHealth: () =>
      apiClient.get<{ status: string; message: string }>('/onboarding/health'),
  },

  // ----------------------------------------------------------------------------
  // Traffic Light Decisions
  // ----------------------------------------------------------------------------
  decisions: {
    getTrafficLight: (data: TrafficLightRequest) =>
      apiClient.post<TrafficLightResponse>('/decisions/traffic-light', data),

    getTrafficLightBatch: (purchases: TrafficLightRequest[]) =>
      apiClient.post<TrafficLightBatchResponse>('/decisions/traffic-light-batch', { purchases }),

    getHealth: () =>
      apiClient.get<{ status: string; message: string }>('/decisions/health'),

    getHistory: (filters?: {
      filter?: 'all' | 'green' | 'yellow' | 'red';
      dateFrom?: string;
      dateTo?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      const params = new URLSearchParams();
      if (filters?.filter && filters.filter !== 'all') {
        params.append('filter', filters.filter);
      }
      if (filters?.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.page) {
        params.append('page', filters.page.toString());
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }
      const queryString = params.toString();
      return apiClient.get<{
        decisions: Array<{
          id: string;
          createdAt: string;
          amount: number;
          description: string;
          decision: 'green' | 'yellow' | 'red';
          confidence: number;
          reasoning: string;
          recommendation: string;
          purchaseImpact?: {
            immediate: number;
            nextWeek: number;
            nextMonth: number;
          };
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/decisions/history${queryString ? `?${queryString}` : ''}`);
    },
  },

  // ----------------------------------------------------------------------------
  // Financial Profile & Analysis
  // ----------------------------------------------------------------------------
  financial: {
    getProfile: () =>
      apiClient.get<{ success: boolean; data: FinancialProfile }>('/financial/profile'),

    getCashFlowForecast: (weeks: number = 4) =>
      apiClient.get<{ success: boolean; data: CashFlowForecast }>('/financial/cash-flow-forecast', {
        params: { weeks },
      }),

    getSafeToSpend: () =>
      apiClient.get<{ success: boolean; data: { safeToSpend: number; lastUpdated: string } }>('/financial/safe-to-spend'),
  },

  // ----------------------------------------------------------------------------
  // Transactions
  // ----------------------------------------------------------------------------
  transactions: {
    getAll: (params?: TransactionListParams) =>
      apiClient.get<{ success: boolean; data: { transactions: Transaction[]; total: number; page: number; limit: number } }>(
        '/transactions',
        { params }
      ),

    getById: (id: string) =>
      apiClient.get<Transaction>(`/transactions/${id}`),

    getMerchantBreakdown: (params: {
      category: CategoryMerchantBreakdown['category'];
      startDate?: string;
      endDate?: string;
    }) =>
      apiClient.get<{ success: boolean; data: CategoryMerchantBreakdown }>(
        '/transactions/category-drilldown/merchants',
        { params }
      ),

    update: (id: string, data: TransactionUpdateRequest) =>
      apiClient.patch<Transaction>(`/transactions/${id}`, data),

    batchUpdate: (updates: Array<{ id: string; data: TransactionUpdateRequest }>) =>
      apiClient.post<{ updated: number; transactions: Transaction[] }>(
        '/transactions/batch-update',
        { updates }
      ),
  },

  // ----------------------------------------------------------------------------
  // Intelligence & Insights
  // ----------------------------------------------------------------------------
  intelligence: {
    getSpendingPatterns: () =>
      apiClient.get<{ success: boolean; data: { patterns: SpendingPattern[] } }>('/intelligence/spending-patterns'),

    getInsights: () =>
      apiClient.get<{ success: boolean; data: { insights: UserInsight[] } }>('/intelligence/insights'),

    submitFeedback: (data: { type: string; message: string; metadata?: any }) =>
      apiClient.post('/intelligence/feedback', data),
  },

  // ----------------------------------------------------------------------------
  // Core Subscription Analysis (read-only)
  // ----------------------------------------------------------------------------
  businessSubscriptions: {
    list: (filters?: SubscriptionAnalysisFilters) =>
      apiClient.get<{ success: boolean; data: SubscriptionAnalysisResponse }>(
        '/business/subscriptions',
        { params: filters }
      ),

    getAlerts: () =>
      apiClient.get<{ success: boolean; data: SubscriptionAnalysisAlert[] }>(
        '/business/subscriptions/alerts'
      ),

    getById: (id: string) =>
      apiClient.get<{ success: boolean; data: SubscriptionAnalysisItem }>(
        `/business/subscriptions/${id}`
      ),
  },

  // ----------------------------------------------------------------------------
  // Core Business Health Reports (read-only history)
  // ----------------------------------------------------------------------------
  businessHealthReports: {
    list: (limit?: number) =>
      apiClient.get<{ success: boolean; data: BusinessHealthReportHistory[] }>(
        '/business/health-reports',
        { params: limit ? { limit } : undefined }
      ),

    getLatest: () =>
      apiClient.get<{ success: boolean; data: BusinessHealthReportHistory | null }>(
        '/business/health-reports/latest'
      ),

    getTrend: (months?: number) =>
      apiClient.get<{ success: boolean; data: BusinessHealthReportTrendPoint[] }>(
        '/business/health-reports/trend',
        { params: months ? { months } : undefined }
      ),
  },

  // ----------------------------------------------------------------------------
  // Debts & Credit Cards
  // ----------------------------------------------------------------------------
  debts: {
    getCreditCards: () =>
      apiClient.get<{ success: boolean; cards: Array<{ id: string; name: string; currentBalance: number; creditLimit: number; utilization: number; paymentStrategy: string }> }>('/v2/debts/credit-cards'),
  },

  // ----------------------------------------------------------------------------
  // Fixed Expenses (Annual/Periodic Bills)
  // ----------------------------------------------------------------------------
  expenses: {
    list: () =>
      apiClient.get<{ success: boolean; data: Array<{
        id: string; user_id: string; name: string; amount_cents: number;
        frequency: string; category: string | null; next_due_date: string;
        is_auto_pay: boolean; source: string; confidence: number;
        last_paid_date: string | null; notes: string | null;
        created_at: string; updated_at: string;
      }> }>('/v2/expenses'),

    getUpcoming: (days: number = 30) =>
      apiClient.get<{ success: boolean; data: Array<{
        id: string; user_id: string; name: string; amount_cents: number;
        frequency: string; category: string | null; next_due_date: string;
        is_auto_pay: boolean; source: string; confidence: number;
        last_paid_date: string | null; notes: string | null;
        created_at: string; updated_at: string;
      }> }>(`/v2/expenses/upcoming?days=${days}`),

    getMonthlyTotal: () =>
      apiClient.get<{ success: boolean; data: { monthly_total_cents: number } }>('/v2/expenses/monthly-total'),

    create: (data: { name: string; amount_cents: number; frequency: string; next_due_date: string; category?: string; is_auto_pay?: boolean; source?: string; notes?: string }) =>
      apiClient.post<{ success: boolean; data: any }>('/v2/expenses', data),

    update: (id: string, data: Record<string, any>) =>
      apiClient.put<{ success: boolean; data: any }>(`/v2/expenses/${id}`, data),

    delete: (id: string) =>
      apiClient.delete<{ success: boolean }>(`/v2/expenses/${id}`),

    rollForward: (id: string) =>
      apiClient.post<{ success: boolean; data: any }>(`/v2/expenses/${id}/roll-forward`),
  },

  // ----------------------------------------------------------------------------
  // Forecast Unlock (Progressive Disclosure)
  // ----------------------------------------------------------------------------
  forecast: {
    getUnlockStatus: () =>
      apiClient.get<{ success: boolean; data: ForecastUnlockStatus }>('/v2/forecast/unlock-status'),

    getTierConfig: () =>
      apiClient.get<{ success: boolean; data: { tiers: ForecastTierConfig[] } }>('/v2/forecast/tier-config'),

    evaluate: () =>
      apiClient.post<{ success: boolean; data: ForecastEvaluationResult }>('/v2/forecast/evaluate'),

    recordEngagement: () =>
      apiClient.post<{ success: boolean; message: string }>('/v2/forecast/record-engagement'),

    recordDecision: () =>
      apiClient.post<{ success: boolean; message: string }>('/v2/forecast/record-decision'),

    recordRecommendationFollowed: () =>
      apiClient.post<{ success: boolean; message: string }>('/v2/forecast/record-recommendation-followed'),

    getHealth: () =>
      apiClient.get<{ success: boolean; service: string; status: string }>('/v2/forecast/health'),
  },
};

export default api;
